import { GetListParams, GetOneParams } from 'react-admin';
import { CustomDataProvider } from '../../providers/CustomDataProvider';
import { baseUrl, httpClient } from '../../providers/AppDataProvider';
import { getAuth } from '../../providers/AppAuthProvider';
import axios from 'axios';

const RESOURCE = 'small-banners';

export class SmallBannersDataProvider implements CustomDataProvider {
  // eslint-disable-next-line class-methods-use-this
  async getList(resource: string, params: GetListParams): Promise<any> {
    if (resource !== RESOURCE) return Promise.reject(new Error('Unsupported getList resource'));

    const { filter = {} } = params;
    const platform = filter?.platform || 'ALL';
    const category = filter?.category;

    const url = `${baseUrl}/${RESOURCE}`;
    const { page = 1, perPage = 10 } = params.pagination || {};

    try {
      const { json } = await httpClient(url, {
        method: 'GET',
      });

      const rows: any[] = json?.data ?? [];
      const filteredRows = rows.filter((row: any) => {
        if (platform !== 'ALL') {
          const rowPlatform = row?.platform;
          if (rowPlatform !== platform) return false;
        }
        if (category) {
          const rowCategory = row?.category;
          if (!rowCategory || !rowCategory.includes(category)) {
            return false;
          }
        }
        return true;
      });

      const total = filteredRows.length;
      const allData = filteredRows.map((r: any, i: number) => {
        const row = r?.id != null ? r : { id: `${i}`, ...r };
        return row;
      });
      const startIndex = (page - 1) * perPage;
      const endIndex = startIndex + perPage;
      const data = allData.slice(startIndex, endIndex);
      return { data, total };
    } catch (error) {
      console.error('SmallBannersDataProvider getList error:', error);
      throw error;
    }
  }

  // eslint-disable-next-line class-methods-use-this
  async getOne(resource: string, params: GetOneParams): Promise<any> {
    if (resource !== RESOURCE) return Promise.reject(new Error('Unsupported getOne resource'));

    const url = `${baseUrl}/${RESOURCE}/${params.id}`;

    try {
      const { json } = await httpClient(url);
      const data = json?.data?.[0] ?? json?.data ?? json;

      if (!data) {
        throw new Error('Small banner not found');
      }

      return { data };
    } catch (error) {
      console.error('SmallBannersDataProvider getOne error:', error);
      throw error;
    }
  }

  // eslint-disable-next-line class-methods-use-this
  async create(resource: string, params: any): Promise<any> {
    if (resource !== RESOURCE) return Promise.reject(new Error('Unsupported create resource'));

    const url = `${baseUrl}/${RESOURCE}`;
    const payload = params?.data ?? {};
    if (payload.isActive === true) {
      payload.isActive = 1;
    }
    if (payload.isActive === false) {
      payload.isActive = -1;
    }
    if (payload.showButton === true) {
      payload.showButton = 1;
    }
    if (payload.showButton === false) {
      payload.showButton = 0;
    }
    if (payload.isInternalRoute === true) {
      payload.isInternalRoute = 1;
    }
    if (payload.isInternalRoute === false) {
      payload.isInternalRoute = 0;
    }

    try {
      const { json } = await httpClient(url, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      if (json.data === null && json.errors?.length > 0) {
        throw new Error(json.errors[0].message);
      }
      return { data: json?.data ?? json };
    } catch (error) {
      console.error('SmallBannersDataProvider create error:', error);
      throw error;
    }
  }

  // eslint-disable-next-line class-methods-use-this
  async update(resource: string, params: any): Promise<any> {
    if (resource !== RESOURCE) return Promise.reject(new Error('Unsupported update resource'));

    const url = `${baseUrl}/${RESOURCE}`;
    const payload = params?.data ?? {};
    if (payload.isActive === true) {
      payload.isActive = 1;
    }
    if (payload.isActive === false) {
      payload.isActive = -1;
    }
    if (payload.showButton === true) {
      payload.showButton = 1;
    }
    if (payload.showButton === false) {
      payload.showButton = 0;
    }
    if (payload.isInternalRoute === true) {
      payload.isInternalRoute = 1;
    }
    if (payload.isInternalRoute === false) {
      payload.isInternalRoute = 0;
    }
    try {
      const { json } = await httpClient(url, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });

      return { data: json?.data ?? json };
    } catch (error) {
      console.error('SmallBannersDataProvider update error:', error);
      throw error;
    }
  }

  // eslint-disable-next-line class-methods-use-this
  async delete(resource: string, params: any): Promise<any> {
    if (resource !== RESOURCE) return Promise.reject(new Error('Unsupported delete resource'));

    const url = `${baseUrl}/${RESOURCE}`;
    const payload = params?.data ?? {};
    payload.isActive = -1;

    try {
      const { json } = await httpClient(url, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      return { data: json ?? { id: payload.id } };
    } catch (error) {
      console.error('SmallBannersDataProvider delete error:', error);
      throw error;
    }
  }

  // eslint-disable-next-line class-methods-use-this
  async getUploadCfg(resource: string): Promise<any> {
    if (resource !== RESOURCE) return Promise.reject(new Error('Unsupported getUploadCfg'));

    const url = `${baseUrl}/${RESOURCE}/getuploadcfg`;

    const { accessToken } = (await getAuth()) || {};
    if (!accessToken) {
      throw new Error('Access token not obtained, please log in first');
    }

    const headers = {
      Authorization: `Bearer ${accessToken}`,
      Accept: 'application/json',
    };
    try {
      const response = await axios.get<any>(url, { headers });
      return response.data;
    } catch (error) {
      console.error('getUploadCfg fail:', error);
      if (axios.isAxiosError(error)) {
        const errorMessage = error.response?.data?.message || error.message || 'getUploadCfg error';
        throw new Error(errorMessage);
      }
      throw new Error('fail to get getUploadCfg');
    }
  }
}

export const smallBannersDataProvider = new SmallBannersDataProvider();
